import { useMemo, useState } from "react";
import { Navigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { activityService } from "@/services/activityService";
import type { ActivityCreate } from "@/types";
import { Permission } from "@/types";
import {
  Button,
  Card,
  EmptyState,
  SearchField,
  ToggleGroup,
} from "@/components/ui";
import { PageFrame } from "@/components/common";
import { Plus, SearchX, Store } from "lucide-react";
import { matchesTextQuery } from "@/lib/textSearch";
import { isActivityListed } from "./activityListFilter";
import { useAuthStore } from "@/store/authStore";
import { ActivityCard, CreateActivityModal } from "./components";
import { toastApiError } from "@/lib/errorMessages";

type ActivityListFilter = "all" | "visible" | "hidden";

/** Search only pays off once the grid no longer fits on one screen. */
const SEARCH_THRESHOLD = 6;

export default function ActivitiesPage() {
  const { t } = useTranslation("activities");
  const { t: tCommon } = useTranslation("common");
  const [filter, setFilter] = useState<ActivityListFilter>("all");
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();
  const { getCurrentUserId, hasPermission, isHost } = useAuthStore();
  const hostMode = isHost();
  const canListAllActivities = hasPermission(Permission.ACTIVITIES_MANAGE_ANY);
  const canCreateActivity = hasPermission(Permission.ACTIVITIES_CREATE);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const {
    data: activities,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      "activities",
      canListAllActivities ? "all" : (getCurrentUserId() ?? "none"),
    ],
    queryFn: () => activityService.getAll(),
  });

  const createMutation = useMutation({
    mutationFn: (data: ActivityCreate) => activityService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      setShowCreateModal(false);
      toast.success(t("list.createdToast"));
    },
    onError: (error) => {
      toastApiError(error, "createActivity");
    },
  });

  const filteredActivities = useMemo(
    () =>
      (activities ?? []).filter((activity) => {
        if (filter === "visible" && !isActivityListed(activity)) return false;
        if (filter === "hidden" && isActivityListed(activity)) return false;
        return matchesTextQuery(search, [
          ...Object.values(activity.name_translations ?? {}),
          activity.city,
          activity.address,
        ]);
      }),
    [activities, filter, search],
  );

  if (hostMode && !isLoading && activities && activities.length === 0) {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }

  const visibleCount = (activities ?? []).filter(isActivityListed).length;

  const pageDescription = canListAllActivities
    ? t("list.descriptionAdmin")
    : t("list.descriptionPartner");

  return (
    <PageFrame
      title={t("list.title")}
      icon={Store}
      description={pageDescription}
      isLoading={isLoading}
      loadingMessage={t("list.loading")}
      error={!!error}
      errorMessage={t("list.errorLoad")}
      onRetry={() => refetch()}
      actions={
        canCreateActivity ? (
          <Button
            onClick={() => setShowCreateModal(true)}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            {t("list.newActivity")}
          </Button>
        ) : undefined
      }
    >
      {canCreateActivity ? (
        <CreateActivityModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => {
            if (createMutation.isPending) return;
            createMutation.mutate(data);
          }}
          isLoading={createMutation.isPending}
        />
      ) : null}

      {activities ? (
        activities.length === 0 ? (
          <Card>
            <EmptyState
              size="md"
              icon={Store}
              title={t("list.emptyTitle")}
              description={
                canCreateActivity
                  ? t("list.emptyDescriptionAdmin")
                  : t("list.emptyDescription")
              }
              action={
                canCreateActivity ? (
                  <Button
                    onClick={() => setShowCreateModal(true)}
                    leftIcon={<Plus className="h-4 w-4" />}
                  >
                    {t("list.createFirst")}
                  </Button>
                ) : undefined
              }
            />
          </Card>
        ) : (
          <>
            <div className="animate-slide-up flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <ToggleGroup
                variant="segmented"
                value={filter}
                onChange={setFilter}
                ariaLabel={t("list.filterAria")}
                className="self-start"
                options={[
                  {
                    value: "all",
                    label: (
                      <FilterLabel
                        label={t("list.filterAll")}
                        count={activities.length}
                      />
                    ),
                  },
                  {
                    value: "visible",
                    label: (
                      <FilterLabel
                        label={t("list.filterVisible")}
                        count={visibleCount}
                      />
                    ),
                  },
                  {
                    value: "hidden",
                    label: (
                      <FilterLabel
                        label={t("list.filterHidden")}
                        count={activities.length - visibleCount}
                      />
                    ),
                  },
                ]}
              />
              {activities.length > SEARCH_THRESHOLD ? (
                <SearchField
                  value={search}
                  onChange={setSearch}
                  placeholder={t("list.searchPlaceholder")}
                  searchLabel={tCommon("search")}
                  clearSearchLabel={tCommon("clearSearch")}
                  className="sm:w-72"
                />
              ) : null}
            </div>
            {filteredActivities.length === 0 ? (
              <Card>
                <EmptyState
                  size="md"
                  icon={SearchX}
                  title={t("list.noMatchesTitle")}
                  description={t("list.noMatchesDescription")}
                  action={
                    <Button
                      variant="outline"
                      onClick={() => {
                        setFilter("all");
                        setSearch("");
                      }}
                    >
                      {t("list.showAll")}
                    </Button>
                  }
                />
              </Card>
            ) : (
              <div className="animate-slide-up grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {filteredActivities.map((activity) => (
                  <ActivityCard
                    key={activity.id}
                    activity={activity}
                    locationLabel={activity.city || undefined}
                  />
                ))}
              </div>
            )}
          </>
        )
      ) : null}
    </PageFrame>
  );
}

function FilterLabel({ label, count }: { label: string; count: number }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      {label}
      <span className="text-xs tabular-nums opacity-60">{count}</span>
    </span>
  );
}
